// Appointment — the booking, in full. Opened from a calendar chip, a table row or a
// service's upcoming bookings: the date tile, the status, the time and length, the
// booking number (copy); the customer (WhatsApp chat); the facts; the notes; who
// cancelled it and why; then only the status actions the server allows (a cancelled
// booking can't change).

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  CalendarClock, CalendarDays, Clock, Phone, StickyNote, Hash, Copy, Check, CheckCheck, ExternalLink, MessageCircle,
  Pencil, UserX, XCircle, ShieldCheck, Loader2, Globe, Bot, MonitorSmartphone, MoreHorizontal, User, X,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Textarea } from '@/components/Textarea';
import {
  appointmentsService, APPT_STATUS_META, to12h, ymd,
  type ApptBooking, type ApptService,
} from '@/services/appointments.service';
import { ActionMenu, type ActionMenuItem } from './Popover';

const SOURCE = {
  ADMIN: { label: 'Added by admin', icon: User },
  WEB: { label: 'Website', icon: Globe },
  KIOSK: { label: 'Kiosk', icon: MonitorSmartphone },
  AI_AGENT: { label: 'AI agent', icon: Bot },
  WHATSAPP: { label: 'WhatsApp', icon: Phone },
} as const;

/* the status's accent for the date tile and the dot */
const ACCENT: Record<string, { band: string; dot: string }> = {
  PENDING: { band: 'bg-amber-500', dot: 'bg-amber-500' },
  CONFIRMED: { band: 'bg-blue-500', dot: 'bg-blue-500' },
  COMPLETED: { band: 'bg-emerald-500', dot: 'bg-emerald-500' },
  CANCELLED: { band: 'bg-rose-500', dot: 'bg-rose-500' },
  NO_SHOW: { band: 'bg-slate-400', dot: 'bg-slate-400' },
};

interface Props {
  open: boolean;
  onClose: () => void;
  booking: ApptBooking | null;
  service?: ApptService | null;   // for the image / price, if available
  onEdit: () => void;
  onChanged: () => void;          // status changed → refresh
}

const minutesBetween = (a: string, b: string) => {
  const [ah, am] = a.split(':').map(Number);
  const [bh, bm] = b.split(':').map(Number);
  return bh * 60 + bm - (ah * 60 + am);
};
const lengthLabel = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`);
const formatPhone = (digits: string) => parsePhoneNumberFromString(`+${digits}`)?.formatInternational() ?? `+${digits}`;

/** "Today", "Tomorrow", "In 3 days", "2 days ago" — against the LOCAL day (UTC is a day behind before 4 am in the Gulf). */
const relativeDay = (date: string) => {
  const today = ymd(new Date());
  if (date === today) return 'Today';
  const days = Math.round((new Date(`${date}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 864e5);
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return days > 0 ? `In ${days} days` : `${-days} days ago`;
};

const relativeTime = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} day${d === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** A small icon action with a title tooltip. */
const IconAction = ({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface text-text-muted transition-colors hover:bg-primary-soft hover:text-primary cursor-pointer"
  >
    {children}
  </button>
);

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4 py-2 text-[13px]">
    <span className="shrink-0 text-text-muted">{label}</span>
    <span className="min-w-0 text-right font-medium text-text-primary">{children}</span>
  </div>
);

const btn = 'h-9 px-3.5 rounded-lg text-[12.5px] font-medium cursor-pointer inline-flex items-center gap-1.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed';
const btnOutline = `${btn} border border-border bg-surface hover:bg-surface-alt text-text-primary`;
const btnPrimary = `${btn} bg-primary text-on-primary font-semibold hover:opacity-90`;
const btnDanger = `${btn} bg-danger text-white font-semibold hover:opacity-90`;

const BookingDetailDialog = ({ open, onClose, booking, service, onEdit, onChanged }: Props) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [reason, setReason] = useState('');
  const [copied, setCopied] = useState(false);

  const close = () => { setCancelMode(false); setReason(''); setCopied(false); onClose(); };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !booking) return null;

  const meta = APPT_STATUS_META[booking.status];
  const accent = ACCENT[booking.status] ?? ACCENT.PENDING;
  const src = SOURCE[booking.source] ?? SOURCE.ADMIN;
  const SrcIcon = src.icon;
  const active = booking.status === 'PENDING' || booking.status === 'CONFIRMED';
  const cancelled = booking.status === 'CANCELLED';

  const d = new Date(`${booking.bookingDate}T00:00:00`);
  const niceDate = d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const mins = booking.durationMinutes || minutesBetween(booking.slotStart, booking.slotEnd);
  const phone = booking.customerPhone ? booking.customerPhone.replace(/\D/g, '') : '';
  const initials = (booking.customerName || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';
  const price = service?.price != null ? `${service.currency ? `${service.currency} ` : ''}${service.price}` : null;

  const copyNo = async () => {
    try { await navigator.clipboard.writeText(booking.bookingNo); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch { /* no clipboard */ }
  };
  const openChat = () => { close(); navigate(`/chats?phone=${phone}`); };
  const openService = () => { if (!booking.serviceUid) return; close(); navigate(`/appointments/services/${booking.serviceUid}`); };

  const setSt = async (st: 'CONFIRMED' | 'COMPLETED' | 'NO_SHOW') => {
    setBusy(true);
    try {
      await appointmentsService.setBookingStatus(booking.bookingUid, st);
      toast.success(`Marked ${APPT_STATUS_META[st].label}`);
      onChanged();
      close();
    } catch (e: any) {
      toast.error(`Couldn't update status — ${e?.response?.data?.message ?? 'Try again.'}`);
    }
    setBusy(false);
  };

  const doCancel = async () => {
    if (!reason.trim()) { toast.error('A cancellation reason is required'); return; }
    setBusy(true);
    try {
      await appointmentsService.cancelBooking(booking.bookingUid, reason.trim());
      toast.success('Booking cancelled');
      onChanged();
      close();
    } catch (e: any) {
      toast.error(`Couldn't cancel — ${e?.response?.data?.message ?? 'Try again.'}`);
    }
    setBusy(false);
  };

  // ⋯ — only what the status allows
  const more: ActionMenuItem[] = [];
  if (active) more.push({ label: 'Edit / reschedule', icon: <Pencil size={14} className="text-primary" />, onSelect: onEdit });
  if (!active && !cancelled) more.push({ label: 'Edit notes', icon: <Pencil size={14} className="text-primary" />, onSelect: onEdit });
  if (active) more.push({ label: 'Mark no-show', icon: <UserX size={14} className="text-slate-500" />, onSelect: () => void setSt('NO_SHOW') });
  if (booking.serviceUid) more.push({ label: 'Open the service', icon: <ExternalLink size={14} />, onSelect: openService });
  more.push({ label: 'Copy booking number', icon: <Copy size={14} />, onSelect: () => void copyNo() });
  if (active) more.push({ label: 'Cancel booking', icon: <XCircle size={14} />, danger: true, separatorAbove: true, onSelect: () => setCancelMode(true) });

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={close}
    >
      <div
        className="relative flex w-full max-w-[560px] max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-surface border border-border shadow-elevated animate-fade-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* header — the date, the service, the time, the status */}
        <div className="border-b border-border px-6 pb-4 pt-5">
          <button
            onClick={close}
            className="absolute top-3.5 right-3.5 flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X size={15} />
          </button>
          <div className="flex items-start gap-4 pr-6">
            <div className="w-[60px] shrink-0 overflow-hidden rounded-xl border border-border bg-surface text-center shadow-soft">
              <div className={clsx('py-0.5 text-[11px] font-semibold text-white', accent.band)}>{d.toLocaleDateString(undefined, { month: 'short' })}</div>
              <div className="pt-1 text-[22px] font-bold leading-none text-text-primary">{d.getDate()}</div>
              <div className="pb-1.5 pt-0.5 text-[11px] text-text-muted">{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={clsx('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold', meta?.className)}>
                  <span className={clsx('h-1.5 w-1.5 rounded-full', accent.dot)} /> {meta?.label ?? booking.status}
                </span>
                <span className="text-[12px] text-text-muted">{relativeDay(booking.bookingDate)}</span>
              </div>
              <h2 className={clsx('mt-1.5 truncate text-[17px] font-bold leading-tight text-text-primary', cancelled && 'line-through decoration-text-muted/60')}>
                {booking.serviceName}
              </h2>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
                <span className="inline-flex items-center gap-1.5 text-text-primary"><Clock size={13} className="text-text-muted" /> {to12h(booking.slotStart)} – {to12h(booking.slotEnd)}</span>
                <span className="text-text-muted">{lengthLabel(mins)}</span>
                <button
                  type="button"
                  onClick={() => void copyNo()}
                  className="inline-flex items-center gap-1 rounded px-1 text-[12px] text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary cursor-pointer"
                  title="Copy booking number"
                  aria-label="Copy booking number"
                >
                  <Hash size={11} />{booking.bookingNo}
                  {copied ? <Check size={11} className="text-success" /> : <Copy size={11} className="opacity-60" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {/* the customer */}
          <section className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-[13px] font-bold text-primary">{initials}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[14.5px] font-semibold text-text-primary">
                  <span className="truncate">{booking.customerName || 'Walk-in'}</span>
                  {booking.phoneVerified && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold text-success" title="The customer verified this phone number">
                      <ShieldCheck size={11} /> Verified
                    </span>
                  )}
                </div>
                <div className="mt-0.5 truncate text-[13px] text-text-muted" dir="ltr">{phone ? formatPhone(phone) : 'No phone'}</div>
                {booking.customerEmail && <div className="truncate text-[13px] text-text-muted">{booking.customerEmail}</div>}
              </div>
              {phone && (
                <IconAction label="Open the WhatsApp chat" onClick={openChat}>
                  <MessageCircle size={16} />
                </IconAction>
              )}
            </div>
          </section>

          {/* the booking */}
          <section className="divide-y divide-border rounded-xl border border-border px-4">
            <Row label="Date"><span className="inline-flex items-center gap-1.5"><CalendarDays size={13} className="text-text-muted" /> {niceDate}</span></Row>
            <Row label="Time">{to12h(booking.slotStart)} – {to12h(booking.slotEnd)} <span className="font-normal text-text-muted">({lengthLabel(mins)})</span></Row>
            <Row label="Service">
              {booking.serviceUid ? (
                <button type="button" className="inline-flex max-w-full items-center gap-1.5 text-primary hover:underline cursor-pointer" onClick={openService}>
                  {service?.imageUrl
                    ? <img src={service.imageUrl} alt="" className="h-5 w-5 rounded object-cover object-top" />
                    : <CalendarClock size={13} />}
                  <span className="truncate">{booking.serviceName}</span>
                </button>
              ) : booking.serviceName}
            </Row>
            {price && <Row label="Price">{price}</Row>}
            <Row label="Channel"><span className="inline-flex items-center gap-1.5"><SrcIcon size={13} className="text-text-muted" /> {src.label}</span></Row>
            {booking.createdAt && (
              <Row label="Booked on">
                <span title={new Date(booking.createdAt).toLocaleString()}>{relativeTime(booking.createdAt)}</span>
              </Row>
            )}
            {booking.timezone && <Row label="Timezone">{booking.timezone}</Row>}
          </section>

          {booking.notes && (
            <section>
              <h3 className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted"><StickyNote size={13} /> Notes</h3>
              <p className="whitespace-pre-wrap break-words rounded-lg bg-surface-alt/50 border border-border px-3 py-2 text-[13px] text-text-primary">{booking.notes}</p>
            </section>
          )}
          {cancelled && booking.cancelReason && (
            <div className="flex gap-2 rounded-lg border border-danger/25 bg-danger-soft px-3 py-2 text-[13px] text-danger">
              <XCircle size={15} className="mt-0.5 shrink-0" />
              <span className="break-words"><b className="font-semibold">{booking.cancelledByName || 'Admin'} cancelled:</b> {booking.cancelReason}</span>
            </div>
          )}
          {cancelMode && (
            <div className="space-y-1.5">
              <label htmlFor="appt-detail-cancel-reason" className="text-[12px] font-semibold text-text-primary">
                Reason for cancellation <span className="text-danger">*</span>
              </label>
              <Textarea id="appt-detail-cancel-reason" rows={3} autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer asked to cancel" />
              <p className="text-[11.5px] text-text-muted">The customer's slot is freed and the cancellation message (if switched on) is sent.</p>
            </div>
          )}
        </div>

        {/* actions */}
        <div className="flex items-center justify-between gap-2 border-t border-border bg-surface-alt/30 px-6 py-3">
          {cancelMode ? (
            <>
              <span />
              <div className="flex gap-2">
                <button type="button" className={btnOutline} onClick={() => { setCancelMode(false); setReason(''); }}>Back</button>
                <button type="button" className={btnDanger} onClick={doCancel} disabled={busy}>
                  {busy && <Loader2 size={14} className="animate-spin" />} Cancel booking
                </button>
              </div>
            </>
          ) : (
            <>
              <ActionMenu
                align="left"
                side="top"
                trigger={<button type="button" className={btnOutline} aria-label="More actions"><MoreHorizontal size={15} /> More</button>}
                items={more}
              />
              <div className="flex gap-2">
                {booking.status === 'PENDING' && (
                  <button type="button" className={btnOutline} onClick={() => void setSt('CONFIRMED')} disabled={busy}>
                    <Check size={14} className="text-blue-600" /> Confirm
                  </button>
                )}
                {active ? (
                  <button type="button" className={btnPrimary} onClick={() => void setSt('COMPLETED')} disabled={busy}>
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />} Mark completed
                  </button>
                ) : (
                  <button type="button" className={btnOutline} onClick={close}>Close</button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BookingDetailDialog;
